/**
 * A little DOM, by hand. Text always goes in as text, never as HTML: what
 * the console shows came from the API, and names, details and URLs there
 * were written by whoever holds a key.
 */

import { describeProblem, type Answer, type Problem } from './api';
import { NAME_EMPTY } from './names';

type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, string | number | boolean | null | undefined | EventListener>;

export const h = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: (Child | Child[])[]): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (typeof v === 'function') el.addEventListener(k.replace(/^on/, ''), v);
    else if (k === 'class') el.className = String(v);
    else if (k === 'value' && 'value' in el) (el as HTMLInputElement).value = String(v);
    else if (k === 'checked' && 'checked' in el) (el as HTMLInputElement).checked = Boolean(v);
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    el.append(typeof child === 'string' || typeof child === 'number' ? document.createTextNode(String(child)) : child);
  }
  return el;
};

export const mount = (target: Element, ...children: (Child | Child[])[]): void => {
  target.replaceChildren();
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    target.append(typeof child === 'string' || typeof child === 'number' ? document.createTextNode(String(child)) : child);
  }
};

/** A way out of a problem, offered as a button beside it (the occurrence of a repeated local time). */
export interface FixIt { readonly label: string; readonly act: () => void }

/** A problem shown as the API put it: its detail first, the refusal's fields under it, then any way out. */
export const problemView = (p: Problem, onClose?: () => void, fixes: readonly FixIt[] = []): HTMLElement => {
  const refusal = p.refusal;
  return h('div', { class: `notice ${p.status === 409 ? 'refused' : 'error'}`, role: 'alert' },
    h('div', { class: 'notice-head' },
      h('strong', {}, p.status === 409 ? 'Refused' : p.status === 0 ? 'Unreachable' : `${p.status} ${p.title}`),
      onClose ? h('button', { class: 'ghost small', onclick: onClose, 'aria-label': 'Dismiss' }, '×') : null),
    h('p', { class: 'detail' }, p.detail),
    fixes.length > 0 ? h('div', { class: 'toolbar fix-its' }, ...fixes.map((f) => h('button', { class: 'small', onclick: f.act }, f.label))) : null,
    refusal ? h('dl', { class: 'refusal' },
      ...Object.entries(refusal).flatMap(([k, v]) => [h('dt', {}, k), h('dd', {}, typeof v === 'string' ? v : JSON.stringify(v))])) : null,
    p.leg !== undefined ? h('p', { class: 'hint' }, `at leg ${p.leg}`) : null,
    h('details', {}, h('summary', {}, 'problem+json'), h('pre', {}, JSON.stringify(p, null, 2))));
};

/** Where a view reports: one problem or one success at a time, in the API's words. */
export class Notices {
  readonly el = h('div', { class: 'notices', 'aria-live': 'polite' });

  show(p: Problem, fixes: readonly FixIt[] = []): void {
    mount(this.el, problemView(p, () => this.clear(), fixes));
  }

  ok(text: string): void {
    mount(this.el, h('div', { class: 'notice ok' }, h('div', { class: 'notice-head' }, h('span', {}, text),
      h('button', { class: 'ghost small', onclick: () => this.clear(), 'aria-label': 'Dismiss' }, '×'))));
  }

  clear(): void {
    this.el.replaceChildren();
  }

  /** Show a failed answer; hand back the value of a good one. */
  take<T>(answer: Answer<T>): T | null {
    if (answer.ok) return answer.value;
    this.show(answer.problem);
    return null;
  }
}

export const problemLine = (p: Problem): string => describeProblem(p);

export const short = (hash: string | null | undefined, n = 8): string => (hash ? hash.slice(0, n) : '—');

export const when = (ms: number): string => new Date(ms).toISOString().replace('T', ' ').slice(0, 19);

/**
 * A box for a name. Anything may be typed: leaving the box rewrites it as the
 * name that will be sent (see names.ts), and `hint` says so while it differs
 * from what was typed. `read()` normalises once more for a submit and gives
 * '' when nothing usable was typed; `hint` then says what to type.
 */
export const nameBox = (input: HTMLInputElement, normalise: (raw: string) => string): { hint: HTMLElement; read: () => string } => {
  const hint = h('span', { class: 'hint saved-as', 'aria-live': 'polite', hidden: true });
  const say = (text: string) => { hint.textContent = text; hint.hidden = text === ''; };
  const show = (typed: string) => {
    const out = normalise(typed);
    say(typed.trim() === '' ? '' : out === '' ? NAME_EMPTY : out !== typed ? `saved as ${out}` : '');
  };
  const settle = (): string => {
    const typed = input.value;
    const out = normalise(typed);
    if (out) input.value = out;
    show(typed);
    return out;
  };
  input.removeAttribute('pattern');
  input.addEventListener('input', () => show(input.value));
  input.addEventListener('blur', settle);
  return { hint, read: settle };
};

/** A labelled form field. */
export const field = (label: string, control: HTMLElement, hint?: string, extra?: HTMLElement): HTMLElement =>
  h('label', { class: 'field' }, h('span', { class: 'name' }, label), control, extra ?? null, hint ? h('span', { class: 'hint' }, hint) : null);

export const standingBadge = (standing: string): HTMLElement => h('span', { class: `badge s-${standing}` }, standing);
